import type { GeoPoint } from './types.js';

const EARTH_RADIUS_M = 6_371_000;

/** מרחק בין שתי נקודות במטרים (נוסחת Haversine) */
export function haversineMeters(a: GeoPoint, b: GeoPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function checkPoint(latRaw: string, lngRaw: string): GeoPoint | null {
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

const AT_RE = /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/;
const BANG_RE = /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/;
const QUERY_RE =
  /[?&](?:q|query|ll|destination|center|daddr|saddr)=(-?\d{1,3}\.\d+)(?:,|%2C)\s*(-?\d{1,3}\.\d+)/i;
// קואורדינטות בתוך הנתיב עצמו, כפי ש-"שתף מיקום" של גוגל מפות מייצר אחרי פענוח הקישור המקוצר:
// /maps/search/32.08,+34.78?entry=tts · /maps/place/32.08,34.78 · /maps/dir//32.08,34.78
const PATH_RE =
  /\/maps\/(?:search|place|dir\/+)\/?(-?\d{1,3}\.\d+)(?:,|%2C)(?:\+|%20|\s)*(-?\d{1,3}\.\d+)/i;
const PLAIN_RE = /^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/;

/**
 * חילוץ קואורדינטות מקישור Google Maps בפורמטים הנפוצים.
 * קישורים מקוצרים (maps.app.goo.gl) אינם מכילים קואורדינטות — הם מפוענחים בשרת.
 */
export function parseMapsUrl(url: string | null | undefined): GeoPoint | null {
  if (!url) return null;
  const s = url.trim();
  if (!s) return null;
  for (const re of [BANG_RE, AT_RE, QUERY_RE, PATH_RE, PLAIN_RE]) {
    const m = re.exec(s);
    if (m && m[1] && m[2]) {
      const p = checkPoint(m[1], m[2]);
      if (p) return p;
    }
  }
  return null;
}

/** האם זהו קישור מפות מקוצר שדורש פענוח בשרת */
export function isShortMapsUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const s = url.trim();
  return /(?:^|\/\/)(?:maps\.app\.goo\.gl|goo\.gl|g\.co)\//i.test(s) && parseMapsUrl(s) === null;
}

const MAPS_URL_IN_TEXT_RE =
  /https?:\/\/(?:www\.)?(?:(?:[a-z]{2,3}\.)?google\.[a-z.]+\/maps\S*|maps\.google\.[a-z.]+\S*|maps\.app\.goo\.gl\/\S+|goo\.gl\/maps\/\S+|g\.co\/(?:kgs|maps)\/\S+)/i;

/** מחלץ קישור Google Maps ראשון מתוך טקסט חופשי (למשל הודעה למפקדה), אם קיים */
export function extractMapsUrl(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = MAPS_URL_IN_TEXT_RE.exec(text);
  if (!m) return null;
  // כולל סימני עיצוב (* _ ~ ^) — קישור שעטוף בהדגשה/קו תחתון לא יתפוס את הסימן הסוגר
  return m[0].replace(/[),.!?;:'"*_~^]+$/, '');
}
